/**
 * Campo de medicamento do receituário estruturado.
 *
 * Trabalha em três níveis, degradando sem quebrar:
 *   1. Catálogo CMED/ANVISA local — traz a apresentação completa e preenche
 *      concentração, forma farmacêutica, quantidade, via e tarja de uma vez.
 *   2. Princípios ativos da Memed — quando o catálogo ainda não foi importado.
 *   3. Texto livre — quando nenhuma das bases está disponível.
 *
 * @see services/medicationCatalogService.ts
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  MedicationCatalogEntry,
  fetchMedicationCatalogStatus,
  formatPresentation,
  searchMedicationCatalog,
} from '../../services/medicationCatalogService';
import { MemedIngredient, fetchMemedStatus, searchMemedMedications } from '../../services/memedService';

/** Campos do receituário que a escolha do medicamento consegue preencher. */
export interface MedicationSelection {
  medication_name: string;
  active_ingredient?: string | null;
  concentration?: string | null;
  pharmaceutical_form?: string | null;
  quantity?: string | null;
  route?: string | null;
  is_controlled?: boolean;
  is_antimicrobial?: boolean;
  control_class?: string | null;
  sncr_receita_tipo?: string | null;
  catalog_id?: string | null;
  posology_profile_key?: string | null;
}

export interface MedicationAutocompleteFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Recebe todos os campos derivados da apresentação escolhida. */
  onMedicationSelected?: (selection: MedicationSelection) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  inputLabelProps?: React.ComponentProps<typeof TextField>['InputLabelProps'];
}

type Source = 'catalog' | 'memed' | 'none';

/** Resolvido uma vez por sessão: evita uma requisição de status por linha. */
let sourcePromise: Promise<Source> | null = null;

export function invalidateMedicationCatalogSource(): void {
  sourcePromise = null;
}

function resolveSource(): Promise<Source> {
  if (!sourcePromise) {
    sourcePromise = fetchMedicationCatalogStatus()
      .then(async (status) => {
        if (status?.available) return 'catalog' as Source;
        const memed = await fetchMemedStatus().catch(() => null);
        return memed?.enabled ? ('memed' as Source) : ('none' as Source);
      })
      .catch(() => 'none' as Source);
  }
  return sourcePromise;
}

type Option = MedicationCatalogEntry | MemedIngredient;

function isCatalogEntry(option: any): option is MedicationCatalogEntry {
  return Boolean(option) && typeof option === 'object' && 'product_name' in option;
}

function optionLabel(option: any): string {
  if (typeof option === 'string') return option;
  if (isCatalogEntry(option)) return option.product_name;
  return option?.nome || '';
}

export const MedicationAutocompleteField: React.FC<MedicationAutocompleteFieldProps> = ({
  value,
  onChange,
  onMedicationSelected,
  label = 'Nome do medicamento *',
  size = 'small',
  fullWidth = true,
  inputLabelProps,
}) => {
  const [source, setSource] = useState<Source>('none');
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  /** Selecionar um item dispara onInputChange; sem isso a lista rebuscaria. */
  const skipNextSearch = useRef(false);

  useEffect(() => {
    let alive = true;
    void resolveSource().then((resolved) => {
      if (alive) setSource(resolved);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (source === 'none') return;
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }

    const term = inputValue.trim();
    if (term.length < 3) {
      setOptions([]);
      return;
    }

    let alive = true;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows =
          source === 'catalog' ? await searchMedicationCatalog(term) : await searchMemedMedications(term);
        if (alive) setOptions(rows);
      } catch {
        if (alive) setOptions([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [source, inputValue]);

  const helperText = useMemo(() => {
    if (source === 'catalog') return 'Digite 3+ letras — base ANVISA/CMED';
    if (source === 'memed') return 'Digite 3+ letras para buscar na base da Memed';
    return undefined;
  }, [source]);

  if (source === 'none') {
    return (
      <TextField
        fullWidth={fullWidth}
        size={size}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const handleSelect = (option: any) => {
    if (!option) return;

    if (typeof option === 'string') {
      onChange(option);
      return;
    }

    skipNextSearch.current = true;

    if (isCatalogEntry(option)) {
      onChange(option.product_name);
      onMedicationSelected?.({
        medication_name: option.product_name,
        active_ingredient: option.active_ingredient,
        concentration: option.concentration,
        pharmaceutical_form: option.pharmaceutical_form,
        quantity: option.package_quantity,
        route: option.default_route,
        is_controlled: option.is_controlled,
        is_antimicrobial: option.is_antimicrobial,
        control_class: option.control_class,
        sncr_receita_tipo: option.sncr_receita_tipo,
        catalog_id: option.id,
        posology_profile_key: option.posology_profile_key,
      });
      return;
    }

    onChange(option.nome);
    onMedicationSelected?.({ medication_name: option.nome, active_ingredient: option.nome });
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      options={options}
      filterOptions={(x) => x}
      loading={loading}
      loadingText={source === 'catalog' ? 'Buscando na base ANVISA…' : 'Buscando na Memed…'}
      noOptionsText={inputValue.trim().length < 3 ? 'Digite 3+ letras' : 'Nada encontrado'}
      inputValue={inputValue}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option: any, selected: any) => optionLabel(option) === optionLabel(selected)}
      onInputChange={(_, next, reason) => {
        setInputValue(next);
        if (reason === 'input' || reason === 'clear') onChange(next);
      }}
      onChange={(_, option: any) => handleSelect(option)}
      renderOption={(props, option: any) => {
        const { key, ...rest } = props as any;
        if (!isCatalogEntry(option)) {
          return (
            <li key={key} {...rest}>
              <Typography variant="body2">{option.nome}</Typography>
            </li>
          );
        }
        const presentation = formatPresentation(option);
        const verified = Boolean(option.has_verified_posology);
        return (
          <li key={key} {...rest}>
            <Box sx={{
              width: '100%',
              py: 0.25,
              px: verified ? 0.75 : 0,
              mx: verified ? -0.75 : 0,
              borderRadius: verified ? 1 : 0,
              bgcolor: verified ? 'rgba(16, 185, 129, 0.08)' : 'transparent',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.product_name}
                </Typography>
                {verified && (
                  <Chip
                    label="Posologia verificada"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: '#D1FAE5',
                      color: '#047857',
                      border: '1px solid #10B981',
                    }}
                  />
                )}
                {option.is_generic && (
                  <Chip label="Genérico" size="small" color="success" variant="outlined"
                    sx={{ height: 18, fontSize: 10 }} />
                )}
                {option.is_controlled && (
                  <Chip label={option.control_class || 'Controlado'} size="small" color="error"
                    variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                )}
                {option.sncr_receita_tipo && (
                  <Chip label={`SNCR ${option.sncr_receita_tipo}`} size="small"
                    sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#3730A3' }} />
                )}
                {option.is_antimicrobial && (
                  <Chip label="Antimicrobiano" size="small" color="warning" variant="outlined"
                    sx={{ height: 18, fontSize: 10 }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {presentation || option.presentation_raw}
                {option.laboratory ? ` — ${option.laboratory}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          helperText={helperText}
          InputLabelProps={inputLabelProps}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default MedicationAutocompleteField;
